import React from 'react';
import { Text, TextSize, TextWeight, TextColor, TextProps, TextBody } from '@components/text/Text';

export const BODY_SIZES = ['small', 'medium', 'large'] as const satisfies readonly TextSize[];
export const BODY_WEIGHTS = [
  'bold',
  'semibold',
  'regular',
  'light',
] as const satisfies readonly TextWeight[];
export const BODY_COLORS = [
  'primary',
  'secondary',
  'tertiary',
  'critical',
  'success',
  'info',
  'disabled',
  'label-primary',
  'primary-interactive',
  'foreverwhite',
] as const satisfies readonly TextColor[];

type SupportedTextWeight = (typeof BODY_WEIGHTS)[number];
type SupportedTextSize = (typeof BODY_SIZES)[number];
type SupportedTextColor = (typeof BODY_COLORS)[number];

export interface BodyProps
  extends Omit<TextProps, 'component' | 'size' | 'weight' | 'color' | 'variant'> {
  size?: SupportedTextSize;
  weight?: SupportedTextWeight;
  color?: SupportedTextColor;
  variant?: TextBody;
}

/**
 * Body Component
 * @param id - A unique identifier for the element.
 * @param children - Any React.ReactNode component.
 * @param className - Any additional classname to the component.
 * @param size - Defines the size of the text.
 * @param variant - Choose between any of the Typography available variants.
 * @param weight - Defines the weight of the text.
 * @param color - Defines the color of the text.
 * @param align - Defines the alignment of the text.
 * @param lineClamp - Visually truncate the text to the specified number of lines. Can set to `true` if you want to set the actual number from another stylesheet.
 * @param overflow - How truncation is handled when text overflows the line.
 */
export const Body: React.FC<BodyProps> = ({
  id,
  children,
  className,
  size = 'medium',
  variant = 'body-01',
  weight = 'regular',
  color = 'primary',
  align = 'start',
  lineClamp,
  overflowWrap,
  ...rest
}) => {
  return (
    <Text
      id={id}
      variant={variant}
      weight={weight}
      align={align}
      color={color}
      size={size}
      overflowWrap={overflowWrap}
      lineClamp={lineClamp}
      className={className}
      {...rest}>
      {children}
    </Text>
  );
};
